import { ChamberMemberProPaymentScheduleEnum } from "../../../graphql/types";
import {
  applyCmpOrganizationNoun,
  CMP_DEFAULT_ORGANIZATION_NOUN,
  CmpOrganizationNoun,
} from "../../../utils/cmpOrganizationNoun";

export type MemberPaymentScheduleValue =
  | ChamberMemberProPaymentScheduleEnum
  | "";

export type MemberPaymentScheduleResolution = {
  enabledPaymentSchedules: ChamberMemberProPaymentScheduleEnum[];
  paymentSchedule: MemberPaymentScheduleValue;
  resolution:
    | "valid-stored"
    | "sole-enabled"
    | "selection-required"
    | "none-enabled";
};

export type NormalizeMemberPaymentScheduleInput = {
  enabledPaymentSchedules: ReadonlyArray<ChamberMemberProPaymentScheduleEnum>;
  storedPaymentSchedule?: MemberPaymentScheduleValue | null;
};

export const isMemberPaymentSchedule = (
  value: unknown
): value is ChamberMemberProPaymentScheduleEnum =>
  value === ChamberMemberProPaymentScheduleEnum.Annual ||
  value === ChamberMemberProPaymentScheduleEnum.Monthly;

/**
 * The schedules staff can choose for an existing member: the enabled ones plus
 * the member's saved schedule. Disabling a schedule in Chamber Set Up only
 * stops new choices of it; a member keeps the schedule they already pay on.
 */
export const getMemberSelectablePaymentSchedules = ({
  enabledPaymentSchedules,
  savedPaymentSchedule,
}: {
  enabledPaymentSchedules: ReadonlyArray<ChamberMemberProPaymentScheduleEnum>;
  savedPaymentSchedule?: MemberPaymentScheduleValue | null;
}): ChamberMemberProPaymentScheduleEnum[] =>
  isMemberPaymentSchedule(savedPaymentSchedule) &&
  !enabledPaymentSchedules.includes(savedPaymentSchedule)
    ? [...enabledPaymentSchedules, savedPaymentSchedule]
    : [...enabledPaymentSchedules];

/**
 * Resolves the only payment-schedule value that may cross a member mutation
 * boundary. A still-enabled stored value wins. Otherwise a sole enabled value
 * is safe to select automatically. Multiple values require a staff choice and
 * no values fail closed.
 */
export const normalizeMemberPaymentSchedule = ({
  enabledPaymentSchedules,
  storedPaymentSchedule,
}: NormalizeMemberPaymentScheduleInput): MemberPaymentScheduleResolution => {
  const normalizedEnabledPaymentSchedules = Array.from(
    new Set(enabledPaymentSchedules.filter(isMemberPaymentSchedule))
  );

  if (
    storedPaymentSchedule &&
    normalizedEnabledPaymentSchedules.includes(storedPaymentSchedule)
  ) {
    return {
      enabledPaymentSchedules: normalizedEnabledPaymentSchedules,
      paymentSchedule: storedPaymentSchedule,
      resolution: "valid-stored",
    };
  }

  if (normalizedEnabledPaymentSchedules.length === 1) {
    return {
      enabledPaymentSchedules: normalizedEnabledPaymentSchedules,
      paymentSchedule: normalizedEnabledPaymentSchedules[0],
      resolution: "sole-enabled",
    };
  }

  return {
    enabledPaymentSchedules: normalizedEnabledPaymentSchedules,
    paymentSchedule: "",
    resolution:
      normalizedEnabledPaymentSchedules.length > 1
        ? "selection-required"
        : "none-enabled",
  };
};

export const getMemberPaymentScheduleValidationError = (
  resolution: MemberPaymentScheduleResolution
): string | undefined => {
  if (resolution.resolution === "selection-required") {
    return "Select a payment option";
  }

  if (resolution.resolution === "none-enabled") {
    return "No payment option is turned on. Turn on Annually or Monthly in Chamber Set Up > Membership Settings > Membership Payment Option, then come back.";
  }

  return undefined;
};

export const getMemberPaymentScheduleDisplayError = ({
  formError,
  resolution,
}: {
  formError?: string;
  resolution: MemberPaymentScheduleResolution;
}, noun: CmpOrganizationNoun = CMP_DEFAULT_ORGANIZATION_NOUN) => {
  const validationError = getMemberPaymentScheduleValidationError(resolution);
  // MEM-35: an unanswered choice shows its error only after submit. A chamber
  // with no enabled schedule cannot continue, so that message shows at once.
  if (resolution.resolution === "selection-required") {
    return formError ? applyCmpOrganizationNoun(formError, noun) : undefined;
  }
  return validationError
    ? applyCmpOrganizationNoun(formError || validationError, noun)
    : undefined;
};

export const shouldShowMemberPaymentScheduleSelection = (
  _resolution: MemberPaymentScheduleResolution
) =>
  // Even a sole auto-selected value remains visible so staff can verify the
  // schedule that will cross the mutation boundary.
  true;

export type MemberPaymentScheduleMutationGate =
  | {
      blocked: false;
      error?: undefined;
      paymentSchedule: ChamberMemberProPaymentScheduleEnum;
      resolution: MemberPaymentScheduleResolution;
    }
  | {
      blocked: true;
      error: string;
      paymentSchedule?: undefined;
      resolution: MemberPaymentScheduleResolution;
    };

const gateMemberPaymentScheduleMutation = (
  input: NormalizeMemberPaymentScheduleInput
): MemberPaymentScheduleMutationGate => {
  const resolution = normalizeMemberPaymentSchedule(input);
  const error = getMemberPaymentScheduleValidationError(resolution);

  if (error || !isMemberPaymentSchedule(resolution.paymentSchedule)) {
    return {
      blocked: true,
      error: error || "Select a payment option",
      resolution,
    };
  }

  return {
    blocked: false,
    paymentSchedule: resolution.paymentSchedule,
    resolution,
  };
};

/** Create Member's final guard immediately before building GraphQL input. */
export const gateCreateMemberPaymentScheduleMutation =
  gateMemberPaymentScheduleMutation;

/** Update Member's final guard immediately before building GraphQL input. */
export const gateUpdateMemberPaymentScheduleMutation =
  gateMemberPaymentScheduleMutation;
